import { useState } from "react";
import "./ActiveSessionLookup.css";

const API_BASE = process.env.REACT_APP_API_BASE || "http://localhost:4000/api";

function useIpdrExport() {
  const [exportingIndex, setExportingIndex] = useState(null);
  const [exportingAll, setExportingAll] = useState(false);
  const [exportError, setExportError] = useState("");
  const [accessDenied, setAccessDenied] = useState(false);

  const exportIpdr = async (row, index, exportDate, destPort) => {
    setExportError("");
    setExportingIndex(index);
    try {
      const res = await fetch(`${API_BASE}/ipdr-export`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ row, exportDate, destPort }),
      });

      if (res.status === 401) {
        setAccessDenied(true);
        return;
      }

      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        if (!exportDate && /export limit/i.test(data.message || "")) {
          const defaultDate = (row.acctstarttime || "").split(" ")[0];
          const picked = window.prompt(`${data.message}\n\nEnter a date (DD/MM/YYYY) within this session to export:`, defaultDate);
          if (picked) {
            return exportIpdr(row, index, picked, destPort);
          }
        }
        setExportError(data.message || "IPDR export failed");
        return;
      }

      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `IPDR_${row.username}${exportDate ? "_" + exportDate.replace(/\//g, "-") : ""}${destPort ? "_port" + destPort : ""}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      setExportError(err.message);
    } finally {
      setExportingIndex(null);
    }
  };

  const exportAllIpdr = async (rows, destPort) => {
    setExportError("");
    setExportingAll(true);
    try {
      const res = await fetch(`${API_BASE}/ipdr-export-merged`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ rows, destPort }),
      });

      if (res.status === 401) {
        setAccessDenied(true);
        return;
      }

      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        setExportError(data.message || "Merged IPDR export failed");
        return;
      }

      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `IPDR_merged_${rows[0]?.username || "export"}${destPort ? "_port" + destPort : ""}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      setExportError(err.message);
    } finally {
      setExportingAll(false);
    }
  };

  return { exportIpdr, exportingIndex, exportAllIpdr, exportingAll, exportError, accessDenied };
}

function ResultsTable({ dtls, error, exportingIndex, onExport, defaultExportDate, defaultDestPort, onExportAll, exportingAll }) {
  return (
    <div className="x_content">
      {error && <p style={{ color: "red" }}>{error}</p>}
      {dtls.length > 0 && onExportAll && (
        <div style={{ marginBottom: "10px" }}>
          <button className="btn btn-new" disabled={exportingAll} onClick={() => onExportAll(dtls, defaultDestPort)}>
            {exportingAll ? "Exporting merged file..." : `Export All (${dtls.length}) Merged`}
          </button>
        </div>
      )}
      <div className="table-responsive">
        <div className="form_table">
          <table className="table">
            <thead>
              <tr>
                <th style={{ minWidth: "100px" }}>User Id</th>
                <th style={{ minWidth: "200px" }}>acctstarttime</th>
                <th style={{ minWidth: "100px" }}>Time (Minutes)</th>
                <th style={{ minWidth: "200px" }}>acctupdatetime</th>
                <th style={{ minWidth: "100px" }}>NAS IP</th>
                <th style={{ minWidth: "100px" }}>IP Address</th>
                <th style={{ minWidth: "150px" }}>Name</th>
                <th style={{ minWidth: "400px" }}>Address</th>
                <th style={{ minWidth: "100px" }}>Mobile</th>
                <th style={{ minWidth: "150px" }}>ID Number</th>
                <th style={{ minWidth: "100px" }}>Mac Id</th>
                {defaultDestPort ? <th style={{ minWidth: "100px" }}>Port Matches</th> : null}
                <th style={{ minWidth: "120px" }}>IPDR</th>
              </tr>
            </thead>
            <tbody>
              {dtls.map((x, i) => (
                <tr key={i}>
                  <td style={{ minWidth: "100px" }}>{x.username}</td>
                  <td style={{ minWidth: "200px" }}>{x.acctstarttime}</td>
                  <td style={{ minWidth: "100px" }}>{x.acctsessiontime}</td>
                  <td style={{ minWidth: "200px" }}>{x.acctupdatetime}</td>
                  <td style={{ minWidth: "100px" }}>{x.nasipaddress}</td>
                  <td style={{ minWidth: "100px" }}>{x.framedipaddress}</td>
                  <td style={{ minWidth: "150px" }}>{`${x.USER_FNAME} ${x.USER_MNAME} ${x.USER_LNAME}`}</td>
                  <td style={{ minWidth: "400px" }}>{x.USER_ADDRESS}</td>
                  <td style={{ minWidth: "100px" }}>{x.USER_MOBILE}</td>
                  <td style={{ minWidth: "150px" }}>{x.USER_ADHAR}</td>
                  <td style={{ minWidth: "100px" }}>{x.callingstationid}</td>
                  {defaultDestPort ? <td style={{ minWidth: "100px" }}>{x.destPortMatchCount}</td> : null}
                  <td style={{ minWidth: "120px" }}>
                    <button
                      className="btn btn-new"
                      disabled={exportingIndex === i}
                      onClick={() => onExport(x, i, defaultExportDate, defaultDestPort)}
                    >
                      {exportingIndex === i ? "Exporting..." : "Export IPDR"}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

/* eslint-disable no-unused-vars */
function IpSearchPanel({ onAccessDenied }) {
  const [NAS, setNAS] = useState("");
  const [IP, setIP] = useState("");
  const [SEARCH_DATE, setSEARCH_DATE] = useState("");
  const [SEARCH_TIME, setSEARCH_TIME] = useState("");
  const [dtls, setDtls] = useState([]);
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState("");
  const { exportIpdr, exportingIndex, exportError, accessDenied } = useIpdrExport();

  if (accessDenied) onAccessDenied();

  const searchIp = async () => {
    setError("");
    setIsProcessing(true);
    try {
      const res = await fetch(`${API_BASE}/active-session-lookup`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ NAS, IP, SEARCH_DATE, SEARCH_TIME }),
      });

      if (res.status === 401) {
        onAccessDenied();
        return;
      }

      const data = await res.json();

      if (data.Status !== "Success") {
        setError(data.message || data.Status || "Request failed");
        setDtls([]);
        return;
      }

      setDtls(data.dtls);
    } catch (err) {
      setError(err.message);
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <div className="x_panel">
      {/* <div className="x_title">
        <div className="row">
          <div className="col-md-2">
            <label>NAS IP</label>
            <input type="text" className="form-control" placeholder="Enter IP Address" value={NAS} onChange={(e) => setNAS(e.target.value)} />
          </div>
          <div className="col-md-2">
            <label>IP Address</label>
            <input type="text" className="form-control" placeholder="Enter IP Address" value={IP} onChange={(e) => setIP(e.target.value)} />
          </div>
          <div className="col-md-4">
            <div className="col-md-12" style={{ padding: 0 }}>
              <div className="col-md-6">
                <label>Search Date</label>
                <input type="text" className="form-control" placeholder="Search Date" value={SEARCH_DATE} onChange={(e) => setSEARCH_DATE(e.target.value)} />
              </div>
              <div className="col-md-6">
                <label>Search Time</label>
                <input type="text" className="form-control" placeholder="Search Time" value={SEARCH_TIME} onChange={(e) => setSEARCH_TIME(e.target.value)} />
              </div>
            </div>
          </div>
          <div className="col-md-2">
            <br />
            {isProcessing && <img src="../images/refresh.gif" height="15px" width="15px" alt="" />}
            <button className="btn btn-new" disabled={isProcessing} onClick={searchIp}>
              Search
            </button>
          </div>
        </div>
        <div className="clearfix"></div>
      </div>

      <ResultsTable dtls={dtls} error={error || exportError} exportingIndex={exportingIndex} onExport={exportIpdr} /> */}
    </div>
  );
}
/* eslint-enable no-unused-vars */

function UserNasSearchPanel({ onAccessDenied }) {
  const [USERNAME, setUSERNAME] = useState("");
  const [NAS, setNAS] = useState("");
  const [DATE, setDATE] = useState("");
  const [useRange, setUseRange] = useState(false);
  const [START_DATE, setSTART_DATE] = useState("");
  const [END_DATE, setEND_DATE] = useState("");
  const [DEST_PORT, setDEST_PORT] = useState("");
  const [dtls, setDtls] = useState([]);
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState("");
  const { exportIpdr, exportingIndex, exportAllIpdr, exportingAll, exportError, accessDenied } = useIpdrExport();

  if (accessDenied) onAccessDenied();

  const search = async () => {
    setError("");
    setIsProcessing(true);
    try {
      const body = useRange
        ? { USERNAME, NAS, START_DATE, END_DATE, DEST_PORT }
        : { USERNAME, NAS, DATE, DEST_PORT };
      const res = await fetch(`${API_BASE}/user-session-lookup`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(body),
      });

      if (res.status === 401) {
        onAccessDenied();
        return;
      }

      const data = await res.json();

      if (data.Status !== "Success") {
        setError(data.message || data.Status || "Request failed");
        setDtls([]);
        return;
      }

      setDtls(data.dtls);
    } catch (err) {
      setError(err.message);
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <div className="x_panel">
      <div className="x_title">
        <div className="row">
          <div className="col-md-3">
            <label>Username</label>
            <input type="text" className="form-control" placeholder="Enter Username" value={USERNAME} onChange={(e) => setUSERNAME(e.target.value)} />
          </div>
          <div className="col-md-2">
            <label>NAS IP</label>
            <input type="text" className="form-control" placeholder="Enter NAS IP" value={NAS} onChange={(e) => setNAS(e.target.value)} />
          </div>
          {useRange ? (
            <>
              <div className="col-md-2">
                <label>Start Date</label>
                <input type="text" className="form-control" placeholder="DD/MM/YYYY" value={START_DATE} onChange={(e) => setSTART_DATE(e.target.value)} />
              </div>
              <div className="col-md-2">
                <label>End Date</label>
                <input type="text" className="form-control" placeholder="DD/MM/YYYY" value={END_DATE} onChange={(e) => setEND_DATE(e.target.value)} />
              </div>
            </>
          ) : (
            <div className="col-md-2">
              <label>Date</label>
              <input type="text" className="form-control" placeholder="DD/MM/YYYY" value={DATE} onChange={(e) => setDATE(e.target.value)} />
            </div>
          )}
          <div className="col-md-2">
            <label>Destination Port</label>
            <input type="text" className="form-control" placeholder="e.g. 443" value={DEST_PORT} onChange={(e) => setDEST_PORT(e.target.value)} />
          </div>
          <div className="col-md-2">
            <br />
            {isProcessing && <img src="../images/refresh.gif" height="15px" width="15px" alt="" />}
            <button className="btn btn-new" disabled={isProcessing} onClick={search}>
              Search
            </button>
          </div>
          <div className="col-md-12">
            <label style={{ display: "inline-flex", alignItems: "center", gap: "6px", fontWeight: "normal" }}>
              <input type="checkbox" checked={useRange} onChange={(e) => setUseRange(e.target.checked)} />
              Search a date range instead of a single date
            </label>
          </div>
        </div>
        <div className="clearfix"></div>
      </div>

      <ResultsTable
        dtls={dtls}
        error={error || exportError}
        exportingIndex={exportingIndex}
        onExport={exportIpdr}
        defaultExportDate={useRange ? "" : DATE}
        defaultDestPort={DEST_PORT}
        onExportAll={exportAllIpdr}
        exportingAll={exportingAll}
      />
    </div>
  );
}

export default function ActiveSessionLookup({ onSessionExpired }) {
  return (
    <div className="right_col" role="main">
      <div>
        <div className="row">
          <div className="col-md-12 col-sm-12 col-xs-12">
            <IpSearchPanel onAccessDenied={onSessionExpired} />
            <UserNasSearchPanel onAccessDenied={onSessionExpired} />
          </div>
        </div>
      </div>
    </div>
  );
}
